import './sass/main.scss';
import { inject } from '@vercel/analytics';
import canvasDotsBg from './bgCanvas.js';
import setupMobileNav from './nav.js';
import hero from './config/hero';
import about from './config/about';
import contact from './config/contact';
import site from './config/site';
import stats from './config/stats';
import timeline from './config/timeline';
import { publicationsData } from './config/publications';
// events config used on /events page

// Initialize Vercel Analytics
inject();

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

// Respect the visitor's reduced-motion setting: no typewriter, no counter
// animation (values snap to their final state).
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

window.onload = function () {
  canvasDotsBg();
  renderSite();
  setupMobileNav();
  renderHero();
  renderAbout();
  renderStats();
  renderEvents();
  renderContact();
  renderPublications();
  renderTimeline();
  setupPublicationFilters();
  setupPageStatusChecking();
  setupNavHighlight();
  setupFormValidation();
  setupScrollReveal();
  setupParallax();

  // Scroll to hash after DOM is fully rendered (fixes #contact and #publications from subpages)
  if (window.location.hash) {
    setTimeout(() => {
      const el = document.querySelector(window.location.hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }
  typewriterEffect();
};

// ---------------------------------------------------------------------------
// Site-wide: page title + nav
// ---------------------------------------------------------------------------

function renderSite() {
  document.title = site.pageTitle;

  const navUl = document.querySelector('.navigation');
  navUl.innerHTML = site.nav
    .map(
      (item, i) =>
        `<li class="navigation__item${i === 0 ? ' navigation__item--active' : ''}" id="${item.id}"><a href="${item.href}">${item.label}</a></li>`
    )
    .join('');
}

// ---------------------------------------------------------------------------
// Hero section
// ---------------------------------------------------------------------------

function renderHero() {
  document.querySelector('.intro__title-line-1').innerHTML = `${hero.greeting} <span>${hero.name}</span>!`;
  // Line 2 left empty — typewriter fills it
  document.querySelector('.intro__title-line-2').textContent = '';

  document.querySelector('.intro__subtitle').innerHTML =
    `in <a href="${hero.lab.url}" target="_blank" rel="noopener noreferrer" class="intro__subtitle--lab">${hero.lab.name}</a> at <a href="${hero.university.url}" target="_blank" rel="noopener noreferrer" class="intro__subtitle--university">${hero.university.name}</a>`;

  const imagesContainer = document.querySelector('.intro__images');
  imagesContainer.innerHTML = hero.images
    .map(
      (img) =>
        `<div class="intro__image"><div class="profile__picture"><img src="${img.src}" alt="${img.alt}" /></div></div>`
    )
    .join('');

  const ctaContainer = document.querySelector('.intro__cta-container');
  ctaContainer.innerHTML = `
    <a href="${hero.cta.primary.href}" class="intro__cta">
      <div class="intro__cta-button">
        ${hero.cta.primary.text}
        <svg class="intro__cta-arrow" version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <title>arrow-right</title>
          <path d="M11.293 5.707l5.293 5.293h-11.586c-0.552 0-1 0.448-1 1s0.448 1 1 1h11.586l-5.293 5.293c-0.391 0.391-0.391 1.024 0 1.414s1.024 0.391 1.414 0l7-7c0.092-0.092 0.166-0.202 0.217-0.324 0.101-0.245 0.101-0.521 0-0.766-0.049-0.118-0.121-0.228-0.217-0.324l-7-7c-0.391-0.391-1.024-0.391-1.414 0s-0.391 1.024 0 1.414z"></path>
        </svg>
      </div>
    </a>
    <a href="${hero.cta.secondary.file}" download class="intro__cta intro__cta--cv">
      <div class="intro__cta-button">${hero.cta.secondary.text}</div>
    </a>`;
}

// ---------------------------------------------------------------------------
// Typewriter effect
// ---------------------------------------------------------------------------

function typewriterEffect() {
  const el = document.querySelector('.intro__title-line-2');
  const text = hero.title;
  let i = 0;

  if (prefersReducedMotion()) {
    el.textContent = text;
    return;
  }

  // Add cursor
  const cursor = document.createElement('span');
  cursor.className = 'typewriter-cursor';
  cursor.innerHTML = '&nbsp;';
  el.appendChild(cursor);

  function type() {
    if (i < text.length) {
      el.insertBefore(document.createTextNode(text.charAt(i)), cursor);
      i++;
      setTimeout(type, 60 + Math.random() * 40);
    } else {
      // Remove cursor after a pause
      setTimeout(() => {
        cursor.style.animation = 'none';
        cursor.style.opacity = '0';
        cursor.style.transition = 'opacity 0.5s';
      }, 2000);
    }
  }

  // Small delay before starting
  setTimeout(type, 600);
}

// ---------------------------------------------------------------------------
// About / Bio
// ---------------------------------------------------------------------------

function renderAbout() {
  const bioContainer = document.querySelector('.intro__bio');
  bioContainer.innerHTML = about
    .map((paragraph, i) => `<div class="intro__bio-paragraph reveal reveal--delay-${i + 1}"><p>${paragraph}</p></div>`)
    .join('');
}

// ---------------------------------------------------------------------------
// Stats / Counter section
// ---------------------------------------------------------------------------

function renderStats() {
  const section = document.querySelector('.stats-section');
  section.innerHTML = `<div class="stats">
    ${stats
      .map(
        (stat, i) => `
      <div class="stats__item reveal reveal--delay-${i + 1}">
        <div class="stats__number" data-target="${stat.value}" data-suffix="${stat.suffix}">0${stat.suffix}</div>
        <div class="stats__label">${stat.label}</div>
      </div>`
      )
      .join('')}
  </div>`;
}

// ---------------------------------------------------------------------------
// Featured event card on main page
// ---------------------------------------------------------------------------

function renderEvents() {
  const section = document.querySelector('.featured-section');
  if (!section) return;

  section.innerHTML = `
    <a href="/tinytapeout" class="featured-card">
      <div class="featured-card__badge">Featured Event</div>
      <h3 class="featured-card__title">TinyTapeout</h3>
      <p class="featured-card__description">Teaching students to tape out real ASICs.</p>
      <span class="featured-card__link">Learn More &rarr;</span>
    </a>
    <a href="/news" class="featured-more">More News &rarr;</a>`;
}

function animateCounters() {
  const counters = document.querySelectorAll('.stats__number[data-target]');
  counters.forEach((counter) => {
    if (counter.dataset.animated) return;
    const target = parseInt(counter.dataset.target, 10);
    const suffix = counter.dataset.suffix || '';
    const duration = 3000;
    const startTime = performance.now();

    counter.dataset.animated = 'true';

    if (prefersReducedMotion()) {
      counter.textContent = target + suffix;
      return;
    }

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);
      counter.textContent = current + suffix;

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  });
}

// ---------------------------------------------------------------------------
// Timeline section
// ---------------------------------------------------------------------------

function renderTimeline() {
  const container = document.querySelector('.timeline__line');
  if (!container) return;

  container.innerHTML = timeline
    .map((item, i) => {
      const logoHtml = item.logo
        ? `<div class="timeline__logo"><img src="${item.logo}" alt="${item.subtitle}" width="44" height="44" loading="lazy" decoding="async" /></div>`
        : '';

      const contentInner = `
        <div class="timeline__header">
          ${logoHtml}
          <div class="timeline__header-text">
            <div class="timeline__year">${item.year}</div>
            <h3 class="timeline__title">${item.title}</h3>
          </div>
        </div>
        <div class="timeline__subtitle">${item.subtitle}</div>
        ${item.description ? `<p class="timeline__description">${item.description}</p>` : ''}`;

      const contentTag = item.link
        ? `<a href="${item.link}" target="_blank" rel="noopener noreferrer" class="timeline__content timeline__content--link">${contentInner}</a>`
        : `<div class="timeline__content">${contentInner}</div>`;

      return `
      <div class="timeline__item${item.active ? ' timeline__item--active' : ''} reveal${i % 2 === 0 ? '-left' : '-right'} reveal--delay-${(i % 4) + 1}">
        ${contentTag}
      </div>`;
    })
    .join('');
}

// ---------------------------------------------------------------------------
// Contact section
// ---------------------------------------------------------------------------

function renderContact() {
  const section = document.querySelector('.contact');
  section.innerHTML = `
    <h2 class="contact__heading section-heading reveal">${contact.heading}</h2>
    <div class="contact__links reveal reveal--delay-1">
      ${contact.socialLinks
        .map(
          (link) =>
            `<a href="${link.url}" target="_blank" class="contact__link-btn contact__link-btn--icon"><img src="${link.icon}" alt="${link.name}" /></a>`
        )
        .join('')}
    </div>
    <p class="contact__text reveal reveal--delay-2">${contact.blurb}</p>
    <form class="contact__form reveal reveal--delay-3" action="${contact.formEndpoint}" method="POST">
      <input class="contact__form-name" type="text" name="name" placeholder="${contact.formPlaceholders.name}" />
      <input class="contact__form-email" type="email" name="_replyto" placeholder="${contact.formPlaceholders.email}" />
      <textarea class="contact__form-message" name="message" placeholder="${contact.formPlaceholders.message}"></textarea>
      <div class="contact__form-error-submit">
        <div class="form-error">
          <div class="form-error__name">${contact.formErrors.name}</div>
          <div class="form-error__email">${contact.formErrors.email}</div>
          <div class="form-error__msg">${contact.formErrors.message}</div>
        </div>
        <button type="button" class="contact__form-submit-2" id="form-submit">Submit</button>
      </div>
      <p class="contact__form-status" id="form-status" role="status" aria-live="polite"></p>
    </form>`;
}

// ---------------------------------------------------------------------------
// Publications rendering — driven entirely by config/publications.js
// ---------------------------------------------------------------------------

function renderPublications() {
  const container = document.querySelector('.publications__container');
  if (!container) return;

  const sorted = [...publicationsData].sort((a, b) => b.id - a.id);

  container.innerHTML = sorted
    .map((pub, idx) => {
      const isLeft = pub.position === 'left';
      const positionClass = isLeft ? 'project-left' : 'project-right';
      const hasImage = pub.image != null;
      const revealDir = isLeft ? 'reveal-left' : 'reveal-right';

      const containerClasses = [
        'project__image-container',
        isLeft ? 'weather' : 'todo',
        pub.imageBgWhite ? 'project__image-container--white-bg' : '',
      ]
        .filter(Boolean)
        .join(' ');

      const imageClasses = [
        'project__image-image',
        pub.imageContain ? 'project__image-image--contain' : '',
      ]
        .filter(Boolean)
        .join(' ');

      const imageContainer = `<div class="${containerClasses}" style="position: relative;"${pub.preview ? ` data-pub-id="${pub.id}"` : ''}>
        ${
          hasImage
            ? `<div class="${imageClasses}">
                <img src="${pub.image}" alt="${pub.title.replace(/<[^>]*>/g, '')}" loading="lazy" decoding="async" />
              </div>`
            : `<div class="project__image-image" style="background-color: #404472; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative;"></div>
              <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #b29758; font-size: 1.8rem; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; z-index: 10; white-space: nowrap; opacity: 0.9;">Image Coming Soon</div>`
        }
      </div>`;

      const previewButton = pub.preview
        ? `<button type="button" class="project__abstract-btn" data-pub-id="${pub.id}">Abstract</button>`
        : '';

      const projectInfo = `<div class="project__info">
        <h3 class="project__title">${pub.title}</h3>
        <p class="project__description">${pub.description}</p>
        ${pub.authors ? `<p class="project__description" style="margin-top: 1rem; font-style: italic;">${pub.authors}</p>` : ''}
        ${previewButton}
      </div>`;

      return isLeft
        ? `<div class="project ${positionClass} ${revealDir}" data-status="${pub.status}">${imageContainer}${projectInfo}</div>`
        : `<div class="project ${positionClass} ${revealDir}" data-status="${pub.status}">${projectInfo}${imageContainer}</div>`;
    })
    .join('');

  // Wire up Abstract buttons
  container.querySelectorAll('.project__abstract-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const pub = publicationsData.find((p) => p.id === Number(btn.dataset.pubId));
      if (pub && pub.preview) openModal(pub);
    });
  });

  // Wire up image container clicks for preview
  container.querySelectorAll('.project__image-container[data-pub-id]').forEach((el) => {
    el.addEventListener('click', () => {
      const pub = publicationsData.find((p) => p.id === Number(el.dataset.pubId));
      if (pub && pub.preview) openModal(pub);
    });
  });
}

// ---------------------------------------------------------------------------
// Publication filters
// ---------------------------------------------------------------------------

function setupPublicationFilters() {
  const filterButtons = document.querySelectorAll('.publication__filter-btn');
  const publications = document.querySelectorAll('[data-status]');

  publications.forEach((pub) => {
    pub.style.display = pub.dataset.status === 'published' ? '' : 'none';
  });

  filterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const filter = button.dataset.filter;
      filterButtons.forEach((btn) => btn.classList.remove('publication__filter-btn--active'));
      button.classList.add('publication__filter-btn--active');
      document.querySelectorAll('[data-status]').forEach((pub) => {
        pub.style.display = pub.dataset.status === filter ? '' : 'none';
      });
    });
  });
}

// ---------------------------------------------------------------------------
// Generic publication modal with image carousel
// ---------------------------------------------------------------------------

let currentModalImages = [];
let currentImageIndex = 0;

function openModal(pub) {
  const modal = document.getElementById('publication-modal');
  if (!modal || !pub.preview) return;

  // Support single image or array of images
  currentModalImages = Array.isArray(pub.preview.images)
    ? pub.preview.images
    : [pub.preview.image];
  currentImageIndex = 0;

  const modalImageContainer = modal.querySelector('.publication-modal__image');
  updateModalImage(modalImageContainer, pub);

  document.getElementById('modal-title').textContent = pub.title.replace(/<br\s*\/?>/g, ' ');
  document.getElementById('modal-description').textContent = pub.preview.description;

  const links = pub.preview.links || {};
  const actionButton = (url, label, extraAttrs = '') => {
    const enabled = Boolean(url);
    const cls = `publication-modal__action-btn${enabled ? '' : ' publication-modal__action-btn--disabled'}`;
    const disabledAttr = enabled ? '' : ' style="pointer-events:none"';
    return `<a href="${url || '#'}" class="${cls}" target="_blank" rel="noopener noreferrer"${extraAttrs}${disabledAttr}>${label}</a>`;
  };

  let actionsHtml = '';
  if (pub.preview.projectPageUrl) {
    actionsHtml += `<a href="${pub.preview.projectPageUrl}" class="publication-modal__action-btn">View Project Page</a>`;
  }
  actionsHtml += actionButton(links.publisherUrl, 'Go to Publisher');
  actionsHtml += actionButton(links.githubUrl, 'View on GitHub');
  actionsHtml += actionButton(links.pdfUrl, 'Download PDF', ' download');

  const hasAllLinks = links.publisherUrl && links.githubUrl && links.pdfUrl;
  if (!hasAllLinks && pub.preview.reviewNotice) {
    actionsHtml += `<p class="publication-modal__review-notice" style="display:block">${pub.preview.reviewNotice}</p>`;
  }

  document.getElementById('modal-actions').innerHTML = actionsHtml;
  modal.style.display = 'flex';
  document.body.style.overflow = 'hidden';
}

function updateModalImage(container, pub) {
  const img = container.querySelector('#modal-image');
  const title = pub ? pub.title.replace(/<[^>]*>/g, '') : '';
  img.src = currentModalImages[currentImageIndex];
  img.alt = title;

  // Remove old carousel controls
  container.querySelectorAll('.publication-modal__carousel-nav, .publication-modal__carousel-dots').forEach((el) => el.remove());

  // Only show carousel controls if multiple images
  if (currentModalImages.length > 1) {
    // Prev / Next arrows
    const prevBtn = document.createElement('button');
    prevBtn.className = 'publication-modal__carousel-nav publication-modal__carousel-nav--prev';
    prevBtn.innerHTML = '<svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    prevBtn.addEventListener('click', () => {
      currentImageIndex = (currentImageIndex - 1 + currentModalImages.length) % currentModalImages.length;
      img.src = currentModalImages[currentImageIndex];
      updateDots();
    });

    const nextBtn = document.createElement('button');
    nextBtn.className = 'publication-modal__carousel-nav publication-modal__carousel-nav--next';
    nextBtn.innerHTML = '<svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    nextBtn.addEventListener('click', () => {
      currentImageIndex = (currentImageIndex + 1) % currentModalImages.length;
      img.src = currentModalImages[currentImageIndex];
      updateDots();
    });

    // Dot indicators
    const dotsContainer = document.createElement('div');
    dotsContainer.className = 'publication-modal__carousel-dots';
    currentModalImages.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = `publication-modal__carousel-dot${i === 0 ? ' publication-modal__carousel-dot--active' : ''}`;
      dot.addEventListener('click', () => {
        currentImageIndex = i;
        img.src = currentModalImages[currentImageIndex];
        updateDots();
      });
      dotsContainer.appendChild(dot);
    });

    container.appendChild(prevBtn);
    container.appendChild(nextBtn);
    container.appendChild(dotsContainer);

    function updateDots() {
      dotsContainer.querySelectorAll('.publication-modal__carousel-dot').forEach((d, i) => {
        d.classList.toggle('publication-modal__carousel-dot--active', i === currentImageIndex);
      });
    }
  }
}

function closeModal() {
  const modal = document.getElementById('publication-modal');
  if (modal) {
    modal.style.display = 'none';
    document.body.style.overflow = '';
  }
}

window.addEventListener('click', (e) => {
  if (e.target.id === 'publication-modal') closeModal();
});
document.addEventListener('click', (e) => {
  if (e.target.closest('.publication-modal__close')) closeModal();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeModal();
  // Arrow keys for carousel
  if (document.getElementById('publication-modal').style.display === 'flex') {
    if (e.key === 'ArrowLeft' && currentModalImages.length > 1) {
      currentImageIndex = (currentImageIndex - 1 + currentModalImages.length) % currentModalImages.length;
      document.getElementById('modal-image').src = currentModalImages[currentImageIndex];
      document.querySelectorAll('.publication-modal__carousel-dot').forEach((d, i) => {
        d.classList.toggle('publication-modal__carousel-dot--active', i === currentImageIndex);
      });
    }
    if (e.key === 'ArrowRight' && currentModalImages.length > 1) {
      currentImageIndex = (currentImageIndex + 1) % currentModalImages.length;
      document.getElementById('modal-image').src = currentModalImages[currentImageIndex];
      document.querySelectorAll('.publication-modal__carousel-dot').forEach((d, i) => {
        d.classList.toggle('publication-modal__carousel-dot--active', i === currentImageIndex);
      });
    }
  }
});

// ---------------------------------------------------------------------------
// Page status checking (under-construction redirect)
// ---------------------------------------------------------------------------

function setupPageStatusChecking() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');
    const pageMap = { research: 'projects', react: 'recipes' };

    for (const [file, pageKey] of Object.entries(pageMap)) {
      if (href.includes(`${file}.html`)) {
        const page = site.pages[pageKey];
        if (page && page.status === 'UnderConstruction') {
          e.preventDefault();
          window.location.href = './under-construction.html';
        }
        return;
      }
    }
  });
}

// ---------------------------------------------------------------------------
// Scroll Reveal (IntersectionObserver)
// ---------------------------------------------------------------------------

function setupScrollReveal() {
  const reveals = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--visible');

          // Trigger counter animation when stats section reveals
          if (entry.target.classList.contains('stats-section')) {
            animateCounters();
          }

          observer.unobserve(entry.target);
        }
      });
    },
    { root: null, rootMargin: '0px', threshold: 0.15 }
  );

  reveals.forEach((el) => observer.observe(el));
}

// ---------------------------------------------------------------------------
// Nav highlight on scroll (IntersectionObserver)
// ---------------------------------------------------------------------------

function setupNavHighlight() {
  const navLinks = document.querySelectorAll('.navigation__item');

  function updateActiveNav(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        navLinks.forEach((link) => link.classList.remove('navigation__item--active'));
        const navItem = document.querySelector(`#nav-${entry.target.id}`);
        if (navItem) navItem.classList.add('navigation__item--active');
      }
    });
  }

  const sectionObserver = new IntersectionObserver(updateActiveNav, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  });
  sectionObserver.observe(document.querySelector('#intro'));
  sectionObserver.observe(document.querySelector('#contact'));

  const pubsObserver = new IntersectionObserver(updateActiveNav, {
    root: null,
    rootMargin: '0px',
    threshold: 0.2,
  });
  pubsObserver.observe(document.querySelector('#publications'));
}

// ---------------------------------------------------------------------------
// Parallax — canvas moves slower than content on scroll
// ---------------------------------------------------------------------------

function setupParallax() {
  const canvas = document.querySelector('.main-bg');
  if (!canvas) return;

  // iOS Safari breaks `position: fixed` when you apply a `transform` to the
  // same element — the canvas drifts and the dots stutter. Skip parallax on
  // touch / small viewports; the static background looks fine there.
  const isTouch = window.matchMedia('(hover: none), (max-width: 800px)').matches;
  if (isTouch) return;

  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        const scrollY = window.pageYOffset;
        canvas.style.transform = `translateY(${scrollY * 0.15}px)`;
        ticking = false;
      });
      ticking = true;
    }
  });
}

// ---------------------------------------------------------------------------
// Contact form validation
// ---------------------------------------------------------------------------

function setupFormValidation() {
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  document.querySelector('#form-submit').addEventListener('click', () => {
    const fields = [
      {
        input: document.querySelector('.contact__form-name'),
        error: document.querySelector('.form-error__name'),
        validate: (v) => !!v,
      },
      {
        input: document.querySelector('.contact__form-email'),
        error: document.querySelector('.form-error__email'),
        validate: (v) => emailRegex.test(v),
      },
      {
        input: document.querySelector('.contact__form-message'),
        error: document.querySelector('.form-error__msg'),
        validate: (v) => !!v,
      },
    ];

    const allValid = fields.every((f) => {
      const valid = f.validate(f.input.value);
      f.input.classList.toggle('input-error', !valid);
      f.error.style.display = valid ? 'none' : 'block';
      return valid;
    });

    if (allValid) {
      submitContactForm();
    }
  });
}

// Submit via fetch so the visitor stays on the page, with inline feedback.
async function submitContactForm() {
  const form = document.querySelector('.contact__form');
  const button = document.querySelector('#form-submit');
  const status = document.querySelector('#form-status');

  button.disabled = true;
  status.className = 'contact__form-status';
  status.textContent = '';

  try {
    const res = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Form endpoint returned ${res.status}`);
    status.textContent = 'Thanks — message sent.';
    status.className = 'contact__form-status contact__form-status--ok';
    form.reset();
  } catch {
    status.textContent = "Something went wrong and the message didn't send. Please try again.";
    status.className = 'contact__form-status contact__form-status--err';
  } finally {
    button.disabled = false;
  }
}
